import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosBaseURL from "..";
import { useAuth, AuthContextValue } from "../../providers/AuthProvider";

export const useGetOffTakerDashboardValues = () => {
  const { authString, user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["off taker dashboard values"],
    staleTime: Number(process.env.REACT_APP_STALE_TIME),
    queryFn: async () => {
      const result = await axiosBaseURL.get("/off-taker/dashboard", {
        headers: {
          header: `${user?.userId}/${user?.token}`,
          Authorization: authString,
        },
      });

      return result;
    },
  });
};

export const useOrderList = (options?: {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "get orders",
      options?.search,
      options?.status,
      options?.page,
      options?.limit,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.limit !== undefined)
        queryParams.append("limit", options.limit.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/off-taker/orders?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
    enabled: !!user?.mfiId,
  });
};

export const useMyOrderList = (options?: {
  search?: string;
  status?: string;
  page?: number;
  size?: number;
}) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: [
      "getMyOrders",
      user?.mfiId,
      options?.search,
      options?.status,
      options?.page,
      options?.size,
    ].filter(Boolean),
    staleTime: 120_000,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      queryParams.append("orgId", user?.mfiId ?? "");
      if (options?.page !== undefined)
        queryParams.append("page", options.page.toString());
      if (options?.size !== undefined)
        queryParams.append("size", options.size.toString());
      if (options?.status) queryParams.append("status", options.status); // API expects uppercase
      if (options?.search) queryParams.append("search", options.search);

      const url = `/off-taker/orders?${queryParams.toString()}`;

      const { data } = await axiosBaseURL.get(url, {
        headers: { header: `${user?.userId}/${user?.token}` },
      });

      return data;
    },
    enabled: !!user?.mfiId,
  });
};

export const useOrderDetail = (id: string) => {
  const { authString, user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["order", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/off-taker/orders/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
          Authorization: authString,
        },
      });

      return result;
    },
  });
};

export const useMyOrderDetail = (id: string) => {
  const { authString, user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["order", id],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/off-taker/orders/${id}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
          Authorization: authString,
        },
      });

      return result;
    },
  });
};

export const useGetOrderStatusCount = () => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["orders status"],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(`/off-taker/status`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useGetVCEByOrderId = (orderId?: string) => {
  const { user } = useAuth() as AuthContextValue;

  return useQuery({
    queryKey: ["vce by order id.", orderId],
    staleTime: 120,
    queryFn: async () => {
      const queryString = orderId ? `?orderId=${orderId}` : "";
      const result = await axiosBaseURL.get(`/operation/vce${queryString}`, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      // const _result = {
      //     data: [
      //         { id: 1, fullName: "Seife Hayle", email: "seife@example.com", phoneNumber: "012345678" },
      //         { id: 2, fullName: "Dagmawi Tadesse", email: "dagtad@example.com", phoneNumber: "012345678" },
      //     ]
      // }
      return result;
    },
  });
};

export const useGetBanksByOrderId = (orderId?: string) => {
  const { user } = useAuth() as AuthContextValue;
  return useQuery({
    queryKey: ["useGetBanksByOrderId", orderId],
    staleTime: 120,
    queryFn: async () => {
      const result = await axiosBaseURL.get(
        `/operation/loaner?orderId=${orderId}`,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};

export const useGetOrderById = (id: string) => {
  return useQuery({
    queryKey: ["useGetOrderById", id],
    staleTime: 120,
    queryFn: async () => {
      // const result = await supabase.from('order').select('*, contract(*)').eq('id', id).single();
      // return result;
    },
  });
};

export const useCreateOrder = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (order: any) => {
      order.org_Id = user?.mfiId;
      order.land_size = undefined;
      const result = await axiosBaseURL.post(`/off-taker/createOrder/`, order, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getMyOrders"] });
    },
  });
};

export const useUpdateOrder = () => {
  const { refetch } = useMyOrderList();
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (order: any) => {
      order.org_Id = user?.mfiId;
      order.land_size = undefined;
      const result = await axiosBaseURL.put(
        `/off-taker/order/edit?id=${order.id}`,
        order,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      refetch();
      return result;
    },
  });
};

export const useUpdateOrderStatus = () => {
  const { user } = useAuth() as AuthContextValue;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { orderId: string; status?: string }) => {
      const result = await axiosBaseURL.put(
        `/off-taker/order/${data.orderId}/status`,
        {
          status: data.status,
        },
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get orders"] });
    },
  });
};

// export const useUpdateOrder = () => {
//     const { refetch } = useOrderList();
//     return useMutation({
//         mutationFn: async (order: { id: string, data: UpdateTables<'order'> }) => {
//             const result = await supabase.from('order').update(order.data).eq('id', order.id);
//             if (result.error) {
//                 throw new Error(result.error.message);
//             }

//             refetch();
//             return result;
//         }
//     })
// };

export const useDeleteOrder = () => {
  const { refetch } = useOrderList();
  const { user } = useAuth() as AuthContextValue;
  return useMutation({
    mutationFn: async (id: string) => {
      try {
        const result = await axiosBaseURL.delete(`/order/${id}`, {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        });

        refetch();
        return result;
      } catch (error) {
        return error;
      }
    },
  });
};

export const useAddVCEOrder = () => {
  const { refetch } = useOrderList();
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (vce_order: { orderId: number; userId: number }) => {
      const result = await axiosBaseURL.post(
        `/off-taker/order/vce`,
        vce_order,
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      refetch();
      return result;
    },
  });
};

export const useRemoveVCEOrder = () => {
  const { refetch } = useOrderList();
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await axiosBaseURL.patch(
        `/operation/vce/inactive?id=${id}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      refetch();
      return result;
    },
  });
};

export const useAddFinancialInstituteToOrder = () => {
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (bank_order: { orderId: number; orgId: number }) => {
      const result = await axiosBaseURL.post(`/operation/loan`, bank_order, {
        headers: {
          header: `${user?.userId}/${user?.token}`,
        },
      });

      return result;
    },
  });
};

export const useRemoveBankFromOrder = () => {
  const { user } = useAuth() as AuthContextValue;

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await axiosBaseURL.put(
        `/operation/loaned?id=${id}`,
        {},
        {
          headers: {
            header: `${user?.userId}/${user?.token}`,
          },
        }
      );

      return result;
    },
  });
};
